import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, Star, Trash2, Edit3, Eye, EyeOff } from 'lucide-react';

import PageHeader from '@/components/PageHeader';
import {
  createPlan,
  deletePlan,
  listPlans,
  updatePlan,
  type PlanInput,
  type PlanItem,
} from '@/lib/operatorApi';
import { formatKop, kopToRub } from '@/lib/format';
import {
  FEATURES_CATALOG,
  FEATURE_GROUP_LABEL,
  FEATURE_GROUP_ORDER,
  getFeatureDef,
  type FeatureDef,
} from '@/data/features';

// CRUD редактор каталога тарифов. Цены храним в копейках (бэкенд), а в форме
// показываем рубли — преобразование идёт в одну сторону при отправке.
// После любого изменения на бэкенде дёргается invalidatePublicPlansCache()
// + invalidateBillingCache() (см. plans.service), поэтому лендинг и ЛК
// увидят правки в течение ~60 сек.

type EditMode = { kind: 'create' } | { kind: 'edit'; plan: PlanItem } | null;

export default function Plans() {
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditMode>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listPlans()
      .then((r) => setPlans(r.plans))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (plan: PlanItem) => {
    if (!window.confirm(`Удалить тариф «${plan.code}»? Это необратимо.`)) return;
    try {
      await deletePlan(plan.id);
      load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка удаления');
    }
  };

  const togglePublic = async (plan: PlanItem) => {
    try {
      await updatePlan(plan.id, { isPublic: !plan.isPublic });
      load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    }
  };

  const togglePopular = async (plan: PlanItem) => {
    try {
      await updatePlan(plan.id, { isPopular: !plan.isPopular });
      load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Тарифы"
        title="Каталог планов"
        description="Цены, фича-флаги и лимиты. Правки мгновенно инвалидируют публичный кеш и billing-cache — новые условия видны и на лендинге, и в ЛК владельцев."
        actions={
          <div className="flex items-center gap-2">
            <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
              <RefreshCw size={13} strokeWidth={1.75} />
              Обновить
            </button>
            <button type="button" className="btn-primary" onClick={() => setEdit({ kind: 'create' })}>
              <Plus size={13} strokeWidth={1.75} />
              Новый тариф
            </button>
          </div>
        }
      />

      <div className="page-pad">
        {error && (
          <div className="mb-4 border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {error}
          </div>
        )}

        {/* Desktop: таблица */}
        <div className="card hidden lg:block">
          <table className="table-grid">
            <thead>
              <tr>
                <th>Код</th>
                <th>Название</th>
                <th className="text-right">Месяц</th>
                <th className="text-right">Год</th>
                <th>Лимиты</th>
                <th>Флаги</th>
                <th>Статус</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id}>
                  <td>
                    <span className="font-mono text-[12px]">{p.code}</span>
                    {p.isPopular && (
                      <span className="ml-2 inline-flex items-center gap-1 bg-orange px-1.5 py-[1px] text-[9px] font-medium uppercase tracking-wider text-white">
                        <Star size={9} /> popular
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="font-medium">{p.name}</div>
                    {p.tagline && (
                      <div className="mt-0.5 text-[11px] text-ink/55">{p.tagline}</div>
                    )}
                  </td>
                  <td className="text-right font-mono tabular-nums text-[13px]">
                    {formatKop(p.priceMonthKopecks)}
                  </td>
                  <td className="text-right font-mono tabular-nums text-[13px]">
                    {formatKop(p.priceYearKopecks)}
                  </td>
                  <td className="text-[11px] text-ink/70">
                    <div>Заведений: {p.maxEstablishments ?? '∞'}</div>
                    <div>Касс: {p.maxCashiers ?? '∞'}</div>
                    <div>Пользователей: {p.maxUsers ?? '∞'}</div>
                  </td>
                  <td className="text-[11px] text-ink/70">
                    {p.features.length === 0 ? (
                      <span className="text-ink/40">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {p.features.map((code) => {
                          const def = getFeatureDef(code);
                          return (
                            <span
                              key={code}
                              title={def?.description ?? code}
                              className="inline-block bg-line/60 px-1.5 py-[2px] text-[10px]"
                            >
                              {def?.name ?? code}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => togglePublic(p)}
                      className="inline-flex items-center gap-1 text-[11px] text-ink/70 hover:text-ink"
                      title="Публикация на лендинге"
                    >
                      {p.isPublic ? (
                        <>
                          <Eye size={12} /> Публичный
                        </>
                      ) : (
                        <>
                          <EyeOff size={12} /> Скрыт
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => togglePopular(p)}
                      className="mt-1 block text-[11px] text-ink/70 hover:text-ink"
                      title="Отметить как популярный (Popular)"
                    >
                      <Star size={11} className={p.isPopular ? 'inline fill-orange text-orange' : 'inline'} />{' '}
                      {p.isPopular ? 'Popular' : 'Обычный'}
                    </button>
                  </td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="mr-3 text-[12px] text-ink/70 hover:text-ink"
                      onClick={() => setEdit({ kind: 'edit', plan: p })}
                    >
                      <Edit3 size={12} className="mr-1 inline" />
                      Править
                    </button>
                    <button
                      type="button"
                      className="text-[12px] text-red-600 hover:underline"
                      onClick={() => handleDelete(p)}
                    >
                      <Trash2 size={12} className="mr-1 inline" />
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && plans.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-ink/50">
                    Каталог пуст. Создайте первый тариф или запустите{' '}
                    <code className="font-mono text-[11px]">npm run seed:plans</code>.
                  </td>
                </tr>
              )}
              {loading && plans.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-ink/40">
                    Загрузка…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile/Tablet: карточки */}
        <ul className="grid grid-cols-1 gap-3 lg:hidden md:grid-cols-2">
          {plans.map((p) => (
            <li key={p.id} className="card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-[15px] font-medium text-ink">{p.name}</div>
                    {p.isPopular && (
                      <span className="inline-flex items-center gap-1 bg-orange px-1.5 py-[1px] text-[9px] font-medium uppercase tracking-wider text-white">
                        <Star size={9} /> popular
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 font-mono text-[11px] text-ink/50">{p.code}</div>
                  {p.tagline && (
                    <div className="mt-1 text-[12px] text-ink/60">{p.tagline}</div>
                  )}
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3 text-[12px]">
                <div>
                  <div className="text-ink/50">Месяц</div>
                  <div className="mt-0.5 font-mono tabular-nums text-[13px]">
                    {formatKop(p.priceMonthKopecks)}
                  </div>
                </div>
                <div>
                  <div className="text-ink/50">Год</div>
                  <div className="mt-0.5 font-mono tabular-nums text-[13px]">
                    {formatKop(p.priceYearKopecks)}
                  </div>
                </div>
              </div>

              <div className="mt-3 text-[12px] text-ink/70">
                <div className="text-ink/50 mb-1">Лимиты</div>
                <div>
                  Заведений: {p.maxEstablishments ?? '∞'} · Касс: {p.maxCashiers ?? '∞'} ·
                  Пользователей: {p.maxUsers ?? '∞'}
                </div>
              </div>

              {p.features.length > 0 && (
                <div className="mt-3">
                  <div className="mb-1 text-[12px] text-ink/50">Флаги</div>
                  <div className="flex flex-wrap gap-1">
                    {p.features.map((code) => {
                      const def = getFeatureDef(code);
                      return (
                        <span
                          key={code}
                          title={def?.description ?? code}
                          className="inline-block bg-line/60 px-1.5 py-[2px] text-[10px]"
                        >
                          {def?.name ?? code}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3 text-[12px]">
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => togglePublic(p)}
                    className="inline-flex items-center gap-1 text-ink/70 hover:text-ink"
                  >
                    {p.isPublic ? (
                      <>
                        <Eye size={12} /> Публичный
                      </>
                    ) : (
                      <>
                        <EyeOff size={12} /> Скрыт
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => togglePopular(p)}
                    className="inline-flex items-center gap-1 text-ink/70 hover:text-ink"
                  >
                    <Star
                      size={11}
                      className={p.isPopular ? 'fill-orange text-orange' : ''}
                    />
                    {p.isPopular ? 'Popular' : 'Обычный'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-ink/70 hover:text-ink"
                    onClick={() => setEdit({ kind: 'edit', plan: p })}
                  >
                    <Edit3 size={12} />
                    Править
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-red-600 hover:underline"
                    onClick={() => handleDelete(p)}
                  >
                    <Trash2 size={12} />
                    Удалить
                  </button>
                </div>
              </div>
            </li>
          ))}
          {!loading && plans.length === 0 && (
            <li className="card col-span-full p-6 text-center text-[13px] text-ink/50">
              Каталог пуст. Создайте первый тариф или запустите{' '}
              <code className="font-mono text-[11px]">npm run seed:plans</code>.
            </li>
          )}
          {loading && plans.length === 0 && (
            <li className="card col-span-full p-6 text-center text-[13px] text-ink/40">
              Загрузка…
            </li>
          )}
        </ul>
      </div>

      {edit && (
        <PlanEditor
          plan={edit.kind === 'edit' ? edit.plan : null}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            load();
          }}
        />
      )}
    </div>
  );
}

// ─── Модалка редактора ──────────────────────────────────────────────────
function PlanEditor({
  plan,
  onClose,
  onSaved,
}: {
  plan: PlanItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!plan;
  const [form, setForm] = useState(() => initialForm(plan));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toInput = (value: number | null): string => (value === null ? '' : String(value));
  const parseNum = (v: string): number => Math.round(Number(v.replace(',', '.')) * 100);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload: PlanInput = {
        code: form.code.trim(),
        name: form.name.trim(),
        tagline: form.tagline.trim() || null,
        description: form.description.trim() || null,
        priceMonthKopecks: parseNum(form.priceMonthRub),
        priceYearKopecks: parseNum(form.priceYearRub),
        maxEstablishments: parseLimit(form.maxEstablishments),
        maxCashiers: parseLimit(form.maxCashiers),
        maxUsers: parseLimit(form.maxUsers),
        extraEstablishmentPriceMonthKopecks: form.extraEstablishmentRub
          ? parseNum(form.extraEstablishmentRub)
          : null,
        features: mergeFeatures(form.featureSet, form.extraFeatures),
        highlights: parseList(form.highlights),
        isPublic: form.isPublic,
        isPopular: form.isPopular,
        sortOrder: Number(form.sortOrder) || 0,
      };
      if (isEdit && plan) {
        await updatePlan(plan.id, payload);
      } else {
        await createPlan(payload);
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/50 p-0 md:p-8">
      <div className="w-full max-w-3xl bg-white shadow-xl min-h-screen md:min-h-0">
        <form onSubmit={handleSubmit}>
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-white px-4 py-3 md:px-6 md:py-4">
            <h2 className="text-[15px] font-medium">
              {isEdit ? `Тариф «${plan?.code}»` : 'Новый тариф'}
            </h2>
            <button type="button" onClick={onClose} className="text-[12px] text-ink/60 hover:text-ink">
              Закрыть
            </button>
          </div>

          <div className="grid gap-4 p-4 md:p-6 md:grid-cols-2">
            {error && (
              <div className="md:col-span-2 border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">
                {error}
              </div>
            )}

            <Labeled label="Код (латиница)">
              <input
                className="input font-mono"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="business"
                required
                disabled={isEdit /* смена кода — через отдельную операцию */}
              />
            </Labeled>
            <Labeled label="Название">
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Business"
                required
              />
            </Labeled>
            <Labeled label="Подзаголовок" className="md:col-span-2">
              <input
                className="input"
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                placeholder="Для ресторанов среднего сегмента"
              />
            </Labeled>
            <Labeled label="Описание" className="md:col-span-2">
              <textarea
                className="input min-h-[80px] resize-y"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Labeled>

            <Labeled label="Цена / мес, ₽">
              <input
                type="number"
                min={0}
                step="0.01"
                className="input tabular-nums"
                value={form.priceMonthRub}
                onChange={(e) => setForm({ ...form, priceMonthRub: e.target.value })}
                required
              />
            </Labeled>
            <Labeled label="Цена / год, ₽">
              <input
                type="number"
                min={0}
                step="0.01"
                className="input tabular-nums"
                value={form.priceYearRub}
                onChange={(e) => setForm({ ...form, priceYearRub: e.target.value })}
                required
              />
            </Labeled>

            <Labeled label="Заведений (пусто = ∞)">
              <input
                type="number"
                min={0}
                className="input tabular-nums"
                value={form.maxEstablishments}
                onChange={(e) => setForm({ ...form, maxEstablishments: e.target.value })}
              />
            </Labeled>
            <Labeled label="Касс (пусто = ∞)">
              <input
                type="number"
                min={0}
                className="input tabular-nums"
                value={form.maxCashiers}
                onChange={(e) => setForm({ ...form, maxCashiers: e.target.value })}
              />
            </Labeled>
            <Labeled label="Пользователей (пусто = ∞)">
              <input
                type="number"
                min={0}
                className="input tabular-nums"
                value={form.maxUsers}
                onChange={(e) => setForm({ ...form, maxUsers: e.target.value })}
              />
            </Labeled>
            <Labeled label="Доп. заведение, ₽/мес (для Network)">
              <input
                type="number"
                min={0}
                step="0.01"
                className="input tabular-nums"
                value={form.extraEstablishmentRub}
                onChange={(e) =>
                  setForm({ ...form, extraEstablishmentRub: e.target.value })
                }
              />
            </Labeled>

            <div className="md:col-span-2">
              <div className="mb-2 flex items-baseline justify-between">
                <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/55">
                  Фичи тарифа
                </div>
                <div className="text-[11px] text-ink/45">
                  Отмечены — доступны на этом тарифе
                </div>
              </div>
              <FeaturePicker
                value={form.featureSet}
                onChange={(next) => setForm({ ...form, featureSet: next })}
              />
              <details className="mt-3 text-[12px] text-ink/55">
                <summary className="cursor-pointer select-none hover:text-ink">
                  Дополнительные флаги вручную (для разработчиков)
                </summary>
                <input
                  className="input mt-2 font-mono text-[12px]"
                  value={form.extraFeatures}
                  onChange={(e) => setForm({ ...form, extraFeatures: e.target.value })}
                  placeholder="my_custom_flag, beta_thing (через запятую)"
                />
                <div className="mt-1 text-[11px] text-ink/45">
                  Используйте, только если флага нет в списке выше. Не забудьте добавить его
                  в каталог <code className="font-mono">sadmin/src/data/features.ts</code>.
                </div>
              </details>
            </div>

            <Labeled
              label="Буллеты для лендинга (по строке)"
              hint="Что входит в тариф — отображается в карточке"
              className="md:col-span-2"
            >
              <textarea
                className="input min-h-[120px] resize-y font-mono text-[12px]"
                value={form.highlights}
                onChange={(e) => setForm({ ...form, highlights: e.target.value })}
                placeholder={'До 3 заведений\nДо 5 касс\nОнлайн-оплата гостем'}
              />
            </Labeled>

            <Labeled label="Сортировка (меньше = левее)">
              <input
                type="number"
                className="input tabular-nums"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
              />
            </Labeled>

            <div className="flex items-center gap-6">
              <label className="inline-flex items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  checked={form.isPublic}
                  onChange={(e) => setForm({ ...form, isPublic: e.target.checked })}
                />
                Публиковать
              </label>
              <label className="inline-flex items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  checked={form.isPopular}
                  onChange={(e) => setForm({ ...form, isPopular: e.target.checked })}
                />
                Популярный
              </label>
            </div>
          </div>

          <div className="sticky bottom-0 flex flex-col gap-3 border-t border-line bg-white px-4 py-3 text-[12px] text-ink/50 md:flex-row md:items-center md:justify-between md:px-6 md:py-4">
            <PricePreview
              monthRub={form.priceMonthRub}
              yearRub={form.priceYearRub}
            />
            <div className="flex items-center gap-2">
              <button type="button" className="btn-ghost flex-1 md:flex-none" onClick={onClose}>
                Отмена
              </button>
              <button type="submit" className="btn-primary flex-1 md:flex-none" disabled={saving}>
                {saving ? 'Сохраняем…' : isEdit ? 'Сохранить' : 'Создать тариф'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Feature picker ────────────────────────────────────────────────────
function FeaturePicker({
  value,
  onChange,
}: {
  value: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const toggle = (code: string) => {
    const next = new Set(value);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange(next);
  };

  return (
    <div className="border border-line">
      {FEATURE_GROUP_ORDER.map((group) => {
        const items = FEATURES_CATALOG.filter((f) => f.group === group);
        if (items.length === 0) return null;
        return (
          <div key={group} className="border-b border-line last:border-b-0">
            <div className="bg-line/30 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.12em] text-ink/60">
              {FEATURE_GROUP_LABEL[group]}
            </div>
            <div className="grid gap-0 sm:grid-cols-2">
              {items.map((f) => (
                <FeatureRow
                  key={f.code}
                  def={f}
                  checked={value.has(f.code)}
                  onToggle={() => toggle(f.code)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FeatureRow({
  def,
  checked,
  onToggle,
}: {
  def: FeatureDef;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className={
        'flex cursor-pointer items-start gap-3 border-line px-4 py-3 transition-colors sm:border-r ' +
        'last:border-r-0 hover:bg-line/20 ' +
        (checked ? 'bg-orange/5' : '')
      }
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="mt-0.5 h-4 w-4 flex-shrink-0 accent-orange"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-ink">{def.name}</span>
          <code className="font-mono text-[10px] text-ink/40">{def.code}</code>
        </div>
        <div className="mt-0.5 text-[12px] leading-snug text-ink/60">{def.description}</div>
      </div>
    </label>
  );
}

function Labeled({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-ink/55">
        {label}
      </div>
      {children}
      {hint && <div className="mt-1 text-[11px] text-ink/45">{hint}</div>}
    </label>
  );
}

function PricePreview({ monthRub, yearRub }: { monthRub: string; yearRub: string }) {
  const month = Number(monthRub.replace(',', '.')) || 0;
  const year = Number(yearRub.replace(',', '.')) || 0;
  const savings = useMemo(() => {
    if (month <= 0 || year <= 0) return 0;
    const monthsInYear = month * 12;
    if (monthsInYear <= year) return 0;
    return Math.round(((monthsInYear - year) / monthsInYear) * 100);
  }, [month, year]);
  return (
    <div className="tabular-nums">
      Экономия при годовой оплате: <strong className="text-ink">{savings}%</strong>
      <span className="ml-2">
        ({formatKop(month * 100)}/мес × 12 = {formatKop(month * 12 * 100)} / год {formatKop(year * 100)})
      </span>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────
interface FormState {
  code: string;
  name: string;
  tagline: string;
  description: string;
  priceMonthRub: string;
  priceYearRub: string;
  maxEstablishments: string;
  maxCashiers: string;
  maxUsers: string;
  extraEstablishmentRub: string;
  /** Отмеченные в сетке известные фичи. */
  featureSet: Set<string>;
  /** Строка «кастомных» флагов через запятую — для фич, которых нет в каталоге. */
  extraFeatures: string;
  highlights: string;
  isPublic: boolean;
  isPopular: boolean;
  sortOrder: string;
}

function initialForm(plan: PlanItem | null): FormState {
  if (!plan) {
    return {
      code: '',
      name: '',
      tagline: '',
      description: '',
      priceMonthRub: '',
      priceYearRub: '',
      maxEstablishments: '',
      maxCashiers: '',
      maxUsers: '',
      extraEstablishmentRub: '',
      featureSet: new Set<string>(),
      extraFeatures: '',
      highlights: '',
      isPublic: true,
      isPopular: false,
      sortOrder: '0',
    };
  }
  // Разделяем known-фичи (показываются в сетке чекбоксов) от unknown —
  // последние уходят в свёрнутое поле «для разработчиков», чтобы не потерять.
  const knownCodes = new Set(FEATURES_CATALOG.map((f) => f.code));
  const featureSet = new Set<string>();
  const extras: string[] = [];
  for (const code of plan.features) {
    if (knownCodes.has(code)) featureSet.add(code);
    else extras.push(code);
  }
  return {
    code: plan.code,
    name: plan.name,
    tagline: plan.tagline ?? '',
    description: plan.description ?? '',
    priceMonthRub: String(kopToRub(plan.priceMonthKopecks)),
    priceYearRub: String(kopToRub(plan.priceYearKopecks)),
    maxEstablishments: plan.maxEstablishments === null ? '' : String(plan.maxEstablishments),
    maxCashiers: plan.maxCashiers === null ? '' : String(plan.maxCashiers),
    maxUsers: plan.maxUsers === null ? '' : String(plan.maxUsers),
    extraEstablishmentRub:
      plan.extraEstablishmentPriceMonthKopecks === null
        ? ''
        : String(kopToRub(plan.extraEstablishmentPriceMonthKopecks)),
    featureSet,
    extraFeatures: extras.join(', '),
    highlights: plan.highlights.join('\n'),
    isPublic: plan.isPublic,
    isPopular: plan.isPopular,
    sortOrder: String(plan.sortOrder),
  };
}

function parseList(raw: string): string[] {
  // Поддерживаем и запятые, и перевод строки — обе формы удобны в textarea/inline.
  return raw
    .split(/[,\n]/g)
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseLimit(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
}

/**
 * Склеивает чекбоксы и «кастомные» флаги в итоговый массив без дублей,
 * сохраняя порядок из FEATURES_CATALOG (чтобы БД хранила стабильный порядок).
 */
function mergeFeatures(known: Set<string>, extras: string): string[] {
  const ordered = FEATURES_CATALOG.map((f) => f.code).filter((c) => known.has(c));
  const extraCodes = parseList(extras);
  const seen = new Set(ordered);
  for (const c of extraCodes) {
    if (!seen.has(c)) {
      ordered.push(c);
      seen.add(c);
    }
  }
  return ordered;
}
